/**
 * =============================================================================
 * PARTICIPANT SUPPORT LOOKUP (BL-ADMIN-PARTICIPANT-SUPPORT-LOOKUP-001)
 * =============================================================================
 *
 * Minimal, read-only operator support panel: enter an EXACT participant email
 * to see that participant's current notification-subscription state (so an
 * operator can answer "am I unsubscribed / why am I not getting emails?"
 * support questions).
 *
 * Scope:
 *   - Read-only lookup of subscription state (the default surface).
 *   - One guarded WRITE: an audited, consent-gated re-subscribe shown ONLY when
 *     the participant is globally unsubscribed (BL-ADMIN-NOTIFICATION-RESUBSCRIBE-001).
 *     v1 is an audited admin re-enable with NO email sent (double-opt-in deferred).
 *   - NO linked questions / replies / queue rows (v1).
 *   - NO participant browse / list / search.
 *   - Every lookup and every re-subscribe is audit-logged server-side; the panel
 *     says so explicitly.
 * =============================================================================
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  Input,
  Panel,
  Text,
  Textarea,
} from '@bigcommerce/big-design';
import type { ParticipantSupportLookupResponse } from '@find-my-part/types';
import { adminApi } from '../../api/client';
import { useSession } from '../../hooks/useSession';
import { StatusChip } from '../common/StatusChip';

/** Map a boolean subscription flag to a BigDesign badge. */
function SubscriptionBadge({ enabled }: { enabled: boolean }): React.ReactElement {
  return (
    <StatusChip
      label={enabled ? 'Subscribed' : 'Unsubscribed'}
      variant={enabled ? 'success' : 'secondary'}
    />
  );
}

/**
 * Admin panel for the read-only participant support lookup.
 */
export function ParticipantLookup(): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ParticipantSupportLookupResponse | null>(null);
  // The exact email the current `result` corresponds to — used as the verified
  // input for a re-subscribe so it can never drift from the looked-up record.
  const [resolvedEmail, setResolvedEmail] = useState('');

  // Re-subscribe (write) state — only ever used when result.globalUnsubscribe.
  const [reason, setReason] = useState('');
  const [requestConfirmed, setRequestConfirmed] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [resubscribeError, setResubscribeError] = useState<string | null>(null);
  const [resubscribeSuccess, setResubscribeSuccess] = useState(false);

  /** Reset the re-subscribe sub-form (e.g. after a new lookup or a success). */
  const resetResubscribeForm = useCallback(() => {
    setReason('');
    setRequestConfirmed(false);
    setResubscribeError(null);
    setResubscribeSuccess(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!token) return;

      const trimmed = email.trim();
      if (!trimmed) {
        setError('Enter a participant email to look up.');
        setResult(null);
        setResolvedEmail('');
        resetResubscribeForm();
        return;
      }

      setLoading(true);
      setError(null);
      resetResubscribeForm();
      try {
        const res = await adminApi.support.participantLookup(token, trimmed);
        if (res.success) {
          setResult(res.data);
          setResolvedEmail(trimmed);
        } else {
          setResult(null);
          setResolvedEmail('');
          setError(res.error?.message ?? 'Lookup failed. Please try again.');
        }
      } catch {
        setResult(null);
        setResolvedEmail('');
        setError('Lookup failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [token, email, resetResubscribeForm]
  );

  const handleResubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!token || !resolvedEmail) return;

      const trimmedReason = reason.trim();
      if (!trimmedReason) {
        setResubscribeError('Enter the reason / documented request before re-subscribing.');
        return;
      }
      if (!requestConfirmed) {
        setResubscribeError(
          'Confirm you have a documented request from this participant before re-subscribing.'
        );
        return;
      }

      setResubscribing(true);
      setResubscribeError(null);
      try {
        const res = await adminApi.support.participantResubscribe(token, {
          email: resolvedEmail,
          reason: trimmedReason,
        });
        if (res.success) {
          // Reflect the new state inline so the operator sees the result without
          // re-entering the email.
          setResult(res.data);
          setReason('');
          setRequestConfirmed(false);
          setResubscribeSuccess(true);
        } else {
          setResubscribeError(res.error?.message ?? 'Re-subscribe failed. Please try again.');
        }
      } catch {
        setResubscribeError('Re-subscribe failed. Please try again.');
      } finally {
        setResubscribing(false);
      }
    },
    [token, resolvedEmail, reason, requestConfirmed]
  );

  // Not authenticated — nothing to control (parent handles the login UX).
  if (!isAuthenticated || !token) {
    return null;
  }

  return (
    <Panel header="Participant support lookup" data-testid="participant-lookup-card">
      <Text color="secondary" marginBottom="small">
        Look up a participant&apos;s notification-subscription state by their exact email address to
        help with support questions (for example, &ldquo;why am I not receiving emails?&rdquo;). This
        shows subscription status only — not their questions, replies, or any other data.
      </Text>

      <Box
        backgroundColor="secondary10"
        borderRadius="normal"
        padding="small"
        marginBottom="medium"
        data-testid="participant-lookup-audit-notice"
      >
        <Text marginBottom="none" color="secondary">
          For privacy, every lookup is <strong>audit-logged</strong>. Use it only to respond to a
          participant&apos;s own support request.
        </Text>
      </Box>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Input
            label="Participant email"
            type="email"
            placeholder="participant@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="participant-lookup-email"
          />
        </FormGroup>
        <Flex>
          <FlexItem>
            <Button type="submit" disabled={loading} isLoading={loading} data-testid="participant-lookup-submit">
              Look up
            </Button>
          </FlexItem>
        </Flex>
      </Form>

      {error && (
        <Box marginTop="medium">
          <Text color="danger" data-testid="participant-lookup-error">
            {error}
          </Text>
        </Box>
      )}

      {result && (
        <Box marginTop="medium" data-testid="participant-lookup-result">
          {!result.hasPreferenceRecord && (
            <Text color="secondary" marginBottom="small" data-testid="participant-lookup-defaults-note">
              No saved preferences for this participant — showing the default subscription state
              (all notifications on).
            </Text>
          )}

          {result.globalUnsubscribe && (
            <Box
              backgroundColor="warning10"
              borderRadius="normal"
              padding="small"
              marginBottom="medium"
              data-testid="participant-lookup-global-unsub"
            >
              <Text bold marginBottom="none" color="warning">
                Globally unsubscribed.
              </Text>
              <Text marginBottom="small" color="secondary">
                This participant has opted out of all notifications, so no emails are sent regardless
                of the per-type settings below.
              </Text>

              <Box
                backgroundColor="white"
                borderRadius="normal"
                padding="small"
                marginTop="small"
                data-testid="participant-resubscribe-form"
              >
                <Text bold marginBottom="xSmall">
                  Re-subscribe this participant
                </Text>
                <Text color="secondary" marginBottom="small">
                  Only re-enable notifications when the participant has asked you to. This action is{' '}
                  <strong>audit-logged</strong> and does <strong>not</strong> send them a
                  confirmation email.
                </Text>
                <Form onSubmit={handleResubscribe}>
                  <FormGroup>
                    <Textarea
                      label="Reason / documented request"
                      description="Do not include the participant's email address or other unnecessary personal information in the reason."
                      placeholder="e.g. Participant emailed support on 2026-06-24 asking to re-enable reply notifications."
                      value={reason}
                      rows={3}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="participant-resubscribe-reason"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Checkbox
                      label="I have a documented request from this participant to re-subscribe them."
                      checked={requestConfirmed}
                      onChange={() => setRequestConfirmed((v) => !v)}
                      data-testid="participant-resubscribe-confirm"
                    />
                  </FormGroup>
                  <Flex>
                    <FlexItem>
                      <Button
                        type="submit"
                        disabled={resubscribing || !reason.trim() || !requestConfirmed}
                        isLoading={resubscribing}
                        data-testid="participant-resubscribe-submit"
                      >
                        Re-subscribe
                      </Button>
                    </FlexItem>
                  </Flex>
                </Form>

                {resubscribeError && (
                  <Box marginTop="small">
                    <Text color="danger" data-testid="participant-resubscribe-error">
                      {resubscribeError}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {resubscribeSuccess && !result.globalUnsubscribe && (
            <Box
              backgroundColor="success10"
              borderRadius="normal"
              padding="small"
              marginBottom="medium"
              data-testid="participant-resubscribe-success"
            >
              <Text bold marginBottom="none" color="success">
                Re-subscribed.
              </Text>
              <Text marginBottom="none" color="secondary">
                Notifications are re-enabled for this participant. The action was audit-logged; no
                confirmation email was sent.
              </Text>
            </Box>
          )}

          <Flex flexDirection="column" flexGap="8px">
            <Flex alignItems="center" flexGap="8px">
              <FlexItem flexGrow={1}>
                <Text bold marginBottom="none">
                  All notifications
                </Text>
              </FlexItem>
              <FlexItem>
                <SubscriptionBadge enabled={!result.globalUnsubscribe} />
              </FlexItem>
            </Flex>
            <Flex alignItems="center" flexGap="8px">
              <FlexItem flexGrow={1}>
                <Text marginBottom="none">Reply notifications</Text>
              </FlexItem>
              <FlexItem>
                <SubscriptionBadge enabled={result.replyNotifications} />
              </FlexItem>
            </Flex>
            <Flex alignItems="center" flexGap="8px">
              <FlexItem flexGrow={1}>
                <Text marginBottom="none">Question approved</Text>
              </FlexItem>
              <FlexItem>
                <SubscriptionBadge enabled={result.questionApproved} />
              </FlexItem>
            </Flex>
            <Flex alignItems="center" flexGap="8px">
              <FlexItem flexGrow={1}>
                <Text marginBottom="none">Question resolved</Text>
              </FlexItem>
              <FlexItem>
                <SubscriptionBadge enabled={result.questionResolved} />
              </FlexItem>
            </Flex>
          </Flex>
        </Box>
      )}
    </Panel>
  );
}
